import type { PrimitiveColorTokens } from '../../types';

/** joy-web's foreground grey. `black` is not `#000`: it is an alias of this value. */
const GRAY_14 = '#0A0A0A'; // neutral-950

export const primitiveColorTokens: PrimitiveColorTokens = {
  transparent: 'transparent',
  white: '#FFFFFF',
  // Deprecated alias of `gray14` (dark-mode plan C6.1).
  black: GRAY_14,

  // Monochrome. Tailwind v3 `neutral` steps (dark-mode plan §4.1); tokens that share a step are
  // aliases until C6.1 collapses them.
  gray1: '#FAFAFA', // neutral-50
  gray2: '#F5F5F5', // neutral-100
  gray3: '#E5E5E5', // neutral-200
  gray4: '#E5E5E5', // neutral-200
  gray5: '#D4D4D4', // neutral-300
  gray6: '#A3A3A3', // neutral-400
  gray7: '#A3A3A3', // neutral-400
  gray8: '#737373', // neutral-500
  gray9: '#737373', // neutral-500
  gray10: '#737373', // neutral-500
  gray11: '#525252', // neutral-600
  gray12: '#525252', // neutral-600
  gray13: '#525252', // neutral-600
  gray14: GRAY_14,
  gray15: '#171717', // neutral-900

  // Purple (Joy purple, `primary*`). The brand ramp, brand 50–800 (dark-mode plan §4.2, R11b).
  purple1: '#EEF2FE', // brand-50
  purple2: '#DFE8FE', // brand-100
  purple3: '#C4D3FE', // brand-200
  purple4: '#A1B5FC', // brand-300
  purple5: '#7A8EF9', // brand-400
  purple6: '#5A69F2', // brand-500
  purple7: '#4A4AFF', // brand-600
  purple8: '#3A3BCB', // brand-700
  purple9: '#3032A4', // brand-800

  // Green (positive). Tailwind v3 `teal` by ramp position (dark-mode plan §4.2, R11a).
  green1: '#F0FDFA', // teal-50
  green2: '#CCFBF1', // teal-100
  green3: '#99F6E4', // teal-200
  green4: '#5EEAD4', // teal-300
  green5: '#14B8A6', // teal-500
  green6: '#0D9488', // teal-600
  green7: '#0F766E', // teal-700
  green8: '#134E4A', // teal-900
  green9: '#042F2E', // teal-950

  // Red (negative). Tailwind v3 `red` by ramp position (dark-mode plan §4.2, R11a).
  red1: '#FEF2F2', // red-50
  red2: '#FEE2E2', // red-100
  red3: '#FECACA', // red-200
  red4: '#FCA5A5', // red-300
  red5: '#EF4444', // red-500
  red6: '#DC2626', // red-600
  red7: '#B91C1C', // red-700
  red8: '#7F1D1D', // red-900
  red9: '#450A0A', // red-950

  // Humana (info). No hue of its own: the Tailwind v3 `neutral` step nearest in lightness (dark-mode plan §4.2, R11b).
  humana1: '#FAFAFA', // neutral-50
  humana2: '#F5F5F5', // neutral-100
  humana3: '#E5E5E5', // neutral-200
  humana4: '#D4D4D4', // neutral-300
  humana5: '#A3A3A3', // neutral-400
  humana6: '#737373', // neutral-500
  humana7: '#404040', // neutral-700
  humana8: '#262626', // neutral-800
  humana9: '#171717', // neutral-900

  // Orange (warning). Tailwind v3 `orange` by ramp position (dark-mode plan §4.2, R11a).
  orange1: '#FFF7ED', // orange-50
  orange2: '#FFEDD5', // orange-100
  orange3: '#FED7AA', // orange-200
  orange4: '#FDBA74', // orange-300
  orange5: '#F97316', // orange-500
  orange6: '#EA580C', // orange-600
  orange7: '#C2410C', // orange-700
  orange8: '#7C2D12', // orange-900
  orange9: '#431407', // orange-950

  // Brand Purple
  brandPurple1: 'rgba(251,247,255,1)',
  brandPurple2: 'rgba(241,235,255,1)',
  brandPurple3: 'rgba(229,217,255,1)',
  brandPurple4: 'rgba(95,38,152,1)',
  brandPurple5: 'rgba(51,0,102,1)',
  brandPurple6: 'rgba(32,0,65,1)',

  // New Brand Purple
  newBrandPurple1: '#6B16FF',
  newBrandPurple2: '#4E06F2',
  newBrandPurple3: '#3B00BB',

  // Brand Blue
  brandBlue1: 'rgba(247,252,255,1)',
  brandBlue2: 'rgba(235,247,255,1)',
  brandBlue3: 'rgba(211,231,255,1)',
  brandBlue4: 'rgba(122,152,255,1)',
  brandBlue5: 'rgba(79,107,203,1)',
  brandBlue6: 'rgba(0,8,76,1)',

  // brandBlurple -- selected cards, informational + auto-approval badges. Tints via `fadeColor`.
  brandBlurple: '#4267D4',

  // Brand Green
  brandGreen1: 'rgba(248,255,247,1)',
  brandGreen2: 'rgba(235,255,232,1)',
  brandGreen3: 'rgba(201,250,212,1)',
  brandGreen4: 'rgba(0,146,137,1)',
  brandGreen5: 'rgba(23,111,105,1)',
  brandGreen6: 'rgba(0,39,27,1)',

  // Brand Yellow
  brandYellow1: 'rgba(255, 252, 247, 1)',
  brandYellow2: 'rgba(255,245,229,1)',
  brandYellow3: 'rgba(255,237,209,1)',
  brandYellow4: 'rgba(255,160,13,1)',
  brandYellow5: 'rgba(188,129,41,1)',
  brandYellow6: 'rgba(80,50,2,1)',

  // Brand Red
  brandRed1: 'rgba(255,248,247,1)',
  brandRed2: 'rgba(255,236,235,1)',
  brandRed3: 'rgba(255,215,212,1)',
  brandRed4: 'rgba(255,100,89,1)',
  brandRed5: 'rgba(178,54,74,1)',
  brandRed6: 'rgba(102,0,55,1)',

  // Brand Humana
  brandHumana1: 'rgba(255,243,232,1)',
  brandHumana2: 'rgba(246,217,196,1)',
  brandHumana3: 'rgba(230,182,155,1)',
  brandHumana4: 'rgba(167,118,97,1)',
  brandHumana5: 'rgba(89,50,42,1)',
  brandHumana6: 'rgba(67,33,33,1)',

  // Brand Warm Gray. The Tailwind v3 `neutral` step nearest in lightness (dark-mode plan §4.2, R11b).
  brandWarmGray1: '#F5F5F5', // neutral-100
  brandWarmGray2: '#D4D4D4', // neutral-300
  brandWarmGray3: '#A3A3A3', // neutral-400
  brandWarmGray4: '#737373', // neutral-500
  brandWarmGray5: '#737373', // neutral-500
  brandWarmGray6: '#525252', // neutral-600
  brandWarmGray7: '#262626', // neutral-800

  // Accent Blue
  accentBlue1: 'rgba(247, 252, 255, 1)',
  accentBlue2: 'rgba(193, 230, 255, 1)',
  accentBlue3: 'rgba(117, 156, 255, 1)',
  accentBlue4: 'rgba(73, 77, 203, 1)',
  accentBlue5: 'rgba(29, 32, 122, 1)',
  accentBlue6: 'rgba(0, 8, 76, 1)',

  // Accent Green
  accentGreen1: 'rgba(248, 255, 247, 1)',
  accentGreen2: 'rgba(235, 255, 232, 1)',
  accentGreen3: 'rgba(0, 146, 137, 1)',
  accentGreen4: 'rgba(23, 111, 105, 1)',
  accentGreen5: 'rgba(6, 64, 57, 1)',
  accentGreen6: 'rgba(0, 39, 27, 1)',

  // New/UI Test, annoying that the name is changing
  newUITest1: 'rgba(73, 81, 239, 1)',
  newUIBrandPrimary: '#4A4AFF', // joykit-cn `--brand-highlight` (R11b); `newUITest1` follows in C6.5
  primaryWeak: '#DFE8FE', // brand-100 (R11b)

  // AI
  ai1: '#25AFFA',
  ai2: '#4951EF',
  ai3: '#CE54B3',
};
